import { Box, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { getNextISOWorkTime } from 'utils/date.utils';

interface CountdownProps {
  workDays: string[];
  workHours: number[];
  timeZone: string;
}

const Countdown: React.FC<CountdownProps> = ({ workDays, workHours, timeZone }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [days, setDays] = useState<number>(0);
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const updateTime = (timeInSeconds: number) => {
    const d = Math.floor(timeInSeconds / (24 * 3600));
    const h = Math.floor((timeInSeconds % (24 * 3600)) / 3600);
    const m = Math.floor((timeInSeconds % 3600) / 60);
    const s = Math.floor(timeInSeconds % 60);

    setDays(d);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
  };

  useEffect(() => {
    const getNextTime = () => {
      const nextStartTime = getNextISOWorkTime(currentTime, workDays, workHours);
      if (nextStartTime) {
        const targetDate = new Date(nextStartTime).getTime();
        const currentTimeInMillis = currentTime.getTime();

        const timeDiff = targetDate - currentTimeInMillis;

        if (timeDiff > 0) {
          updateTime(timeDiff / 1000);
          setErrorMessage(undefined);
        } else {
          setErrorMessage('The campaign is currently working.');
        }
      } else {
        setErrorMessage('No valid work time found.');
      }
    };

    getNextTime();

    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [currentTime, workDays, workHours, timeZone]);

  return (
    <Box className={classes.container}>
      {_.isNil(errorMessage) ? (
        <div className={classes.countdownContainer}>
          <Typography className={classes.titleText} mb={'16px'}>
            {t('campaignStartCountdown')}
          </Typography>
          <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
            <Box>
              <Box className={classes.timeBox}>
                <Typography className={cx(classes.time, days > 0 ? 'highlight' : undefined)}>{days}</Typography>
              </Box>
              <Typography className={classes.fieldName}>{t('days')}</Typography>
            </Box>
            <Box>
              <Box className={classes.timeBox}>
                <Typography className={cx(classes.time, days > 0 || hours > 0 ? 'highlight' : undefined)}>{hours}</Typography>
              </Box>
              <Typography className={classes.fieldName}>{t('hours')}</Typography>
            </Box>
            <Box>
              <Box className={classes.timeBox}>
                <Typography className={cx(classes.time, days > 0 || hours > 0 || minutes > 0 ? 'highlight' : undefined)}>{minutes}</Typography>
              </Box>
              <Typography className={classes.fieldName}>{t('minutes')}</Typography>
            </Box>
            <Box>
              <Box className={classes.timeBox}>
                <Typography className={cx(classes.time, days > 0 || hours > 0 || minutes > 0 || seconds > 0 ? 'highlight' : undefined)}>
                  {seconds}
                </Typography>
              </Box>
              <Typography className={classes.fieldName}>{t('seconds')}</Typography>
            </Box>
          </Stack>
        </div>
      ) : null}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    textAlign: 'center',
  },
  countdownContainer: {
    boxShadow: Shadows.GREY_SHADOW_MAIN,
    borderRadius: '10px',
    width: '100%',
    padding: '20px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
    color: theme.palette.greys.medium,
  },
  timeBox: {
    width: '80px',
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.greys.light}`,
    borderRadius: '4px',
  },
  time: {
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 'bold',
    color: theme.palette.greys.light,
    '&.highlight': {
      color: theme.palette.primary.main,
    },
  },
  fieldName: {
    fontSize: theme.typography.pxToRem(16),
    marginTop: '8px',
    fontWeight: 'lighter',
    color: theme.palette.greys.medium,
  },
}));

export default Countdown;
